import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
const BENEFITS = ['Evaluaciones organizadas', 'Pruebas clínicas explicadas', 'Razonamiento paso a paso', 'Intervenciones con criterio'];
export function Hero() {
  return <section className="relative overflow-hidden bg-white pt-4 pb-8 sm:pt-8 sm:pb-12 md:pt-10 md:pb-14" data-id="element-129" data-mp="4lqjixa15f41">
      {/* Soft clinical wash — keeps the white from reading flat */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-white via-mist-100 to-mist-200/80 pointer-events-none" data-id="element-130" data-mp="128pg780q2n1x" />
      <div aria-hidden className="absolute -top-24 left-1/2 -translate-x-1/2 w-[560px] h-[300px] rounded-full bg-clinic/[0.07] blur-[110px] pointer-events-none" data-id="element-131" data-mp="sai535t46z7u" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center" data-id="element-132" data-mp="3d1a4za4glt8j">
        {/* Eyebrow */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex items-center gap-2 mb-3 sm:mb-4" data-id="element-133" data-mp="381nwtf3hn36l">
          <span aria-hidden className="h-px w-5 bg-gold" data-id="element-134" data-mp="3o59chkk9b8f2" />
          <span className="text-[9px] sm:text-[11px] font-bold tracking-[0.16em] uppercase text-petrol leading-tight" data-id="element-135" data-mp="399jnrenosh6n">
            Atlas Clínico · Kinesiología y Fisioterapia
          </span>
          <span aria-hidden className="h-px w-5 bg-gold" data-id="element-136" data-mp="30gztsricb0s5" />
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05,
        duration: 0.35,
        ease: [0.23, 1, 0.32, 1]
      }} className="font-heading font-extrabold tracking-tight text-navy-950 text-[1.6rem] sm:text-[2.4rem] md:text-[2.8rem] leading-[1.09] max-w-[18ch] sm:max-w-[24ch] mb-3 sm:mb-4" data-id="element-137" data-mp="359j8jrsrk1yj">
          Ten claro qué evaluar, qué pruebas realizar y{' '}
          <span className="text-gold-deep" data-id="element-138" data-mp="2lml8gkbazfab">cómo abordar cada caso clínico.</span>
        </motion.h1>

        {/* Support copy */}
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.11,
        duration: 0.32,
        ease: [0.23, 1, 0.32, 1]
      }} className="text-[12.5px] sm:text-[15.5px] text-navy-800/75 leading-relaxed max-w-md sm:max-w-xl mb-5 sm:mb-7" data-id="element-139" data-mp="nat6xa8uyik6">
          Una guía práctica de consulta para conectar evaluación, pruebas
          clínicas, razonamiento e intervención sin depender de apuntes
          dispersos.
        </motion.p>

        {/* Product — visual protagonist */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.16,
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex justify-center w-full mb-4 sm:mb-6" data-id="element-140" data-mp="110wo3msvrm2r">
          <div aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[115%] max-w-[420px] aspect-square rounded-full bg-petrol/[0.10] blur-[70px] pointer-events-none" data-id="element-141" data-mp="3hqagqilkozhv" />
          <img src="https://cdn.magicpatterns.com/uploads/oGvgXBXVGL78eTMFH1YLiL/Diseno_sin_titulo_(3).png" alt="Atlas Clínico — sistema de consulta y razonamiento clínico para Kinesiología y Fisioterapia" width={920} height={920} loading="eager" decoding="async" {...{
          fetchpriority: 'high'
        } as any} className="relative w-full max-w-[265px] sm:max-w-[350px] md:max-w-[410px] drop-shadow-[0_18px_44px_rgba(11,27,51,0.24)]" data-id="element-142" data-mp="218toztcqb6dt" />
        </motion.div>

        {/* Benefit line */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.22,
        duration: 0.32,
        ease: [0.23, 1, 0.32, 1]
      }} className="font-heading font-extrabold uppercase tracking-tight text-navy-950 text-[0.82rem] sm:text-[1.1rem] leading-tight max-w-[22ch] sm:max-w-none mb-4 sm:mb-5" data-id="element-143" data-mp="2tz1zcz10wmhu">
          De la evaluación a la intervención,{' '}
          <span className="text-gold-deep" data-id="element-144" data-mp="3vjrs687e5pqh">paso a paso.</span>
        </motion.p>

        {/* 2×2 mini-benefits */}
        <motion.ul initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.27,
        duration: 0.32,
        ease: [0.23, 1, 0.32, 1]
      }} className="grid grid-cols-2 gap-x-3 gap-y-2 w-full max-w-sm sm:max-w-lg mb-5 sm:mb-6 text-left" data-id="element-145" data-mp="9ah476xnerzr">
          {BENEFITS.map(b => <li key={b} className="flex items-start gap-1.5 text-[11.5px] sm:text-[13.5px] text-navy-900 leading-snug" data-id="element-146" data-mp="3rgc1dsi06jlp">
              <span className="mt-[2px] flex-shrink-0 w-4 h-4 rounded-full bg-clinic/12 border border-clinic/40 flex items-center justify-center" data-id="element-147" data-mp="2yzsw9te6ju19">
                <Check size={9} className="text-clinic-dark" strokeWidth={3} data-id="element-148" data-mp="2tzwqbolx9sj2" />
              </span>
              <span data-id="element-149" data-mp="19hvn0qrrct9c">{b}</span>
            </li>)}
        </motion.ul>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.32,
        duration: 0.32,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex flex-col items-center w-full" data-id="element-150" data-mp="1ea5liv6xv1n8">
          <a href="#pricing" className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy-950 font-extrabold text-[14.5px] sm:text-lg px-6 sm:px-10 py-4 sm:py-5 rounded-full transition-[transform,background-color] duration-200 ease-out hover:scale-[1.02] shadow-[0_10px_30px_rgba(201,168,76,0.35)] w-full max-w-[380px] sm:w-auto leading-tight" data-id="element-151" data-mp="1s959e0mmncic">
            QUIERO ACCEDER A ATLAS CLÍNICO
            <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-200 ease-out flex-shrink-0" data-id="element-152" data-mp="7rqmff1fe685" />
          </a>

          <p className="text-[10.5px] sm:text-xs text-navy-800/60 mt-2.5" data-id="element-153" data-mp="2eg924jn9437t">
            Acceso inmediato · Pago único · 7 días de garantía
          </p>
        </motion.div>
      </div>
    </section>;
}